import {Autocomplete, Box, Chip, Stack, TextField, Typography} from "@mui/material";
import type {AnalystInsight} from "../../../../../sdo/AnalystInsight.ts";
import {fieldSx} from "../resolvers/editors/ResolverUtils.tsx";
import {hintColor} from "../../../../../AppStyles.ts";

interface InsightEditorProps {
    draft: AnalystInsight;
    editing: boolean;
    onChange: (updated: AnalystInsight) => void;
}

const metaLabelSx = {
    color: "#a8b8c0",
    fontSize: "0.75rem",
    fontWeight: 500,
    whiteSpace: "nowrap" as const,
};

const metaValueSx = {
    color: "#f2f5fa",
    fontWeight: 700,
};

// Multiline-friendly variant of fieldSx (drops the fixed 38px height so TextArea can grow)
const multilineFieldSx = {
    ...fieldSx,
    "& .MuiOutlinedInput-root": {
        ...(fieldSx as Record<string, unknown>)["& .MuiOutlinedInput-root"] as object,
        height: "auto",
        padding: "10px 12px",
    },
    "& .MuiOutlinedInput-input": {
        color: "#f2f5fa",
        fontSize: "0.80rem",
        lineHeight: 1.6,
    },
};

function formatDateTime(epochMs: number): string {
    if (!epochMs || epochMs <= 0) return "";
    const d = new Date(epochMs);
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function InsightEditor({draft, editing, onChange}: InsightEditorProps) {
    const upd = <K extends keyof AnalystInsight>(key: K, value: AnalystInsight[K]) => {
        onChange({...draft, [key]: value});
    };

    const handleTagsChange = (newTags: string[]) => {
        // De-duplicate (case-insensitive) and trim
        const seen = new Set<string>();
        const cleaned: string[] = [];
        for (const raw of newTags) {
            const tag = raw.trim();
            if (!tag) continue;
            const key = tag.toLowerCase();
            if (seen.has(key)) continue;
            seen.add(key);
            cleaned.push(tag);
        }
        upd("tags", cleaned);
    };

    if (!editing) {
        // Snippet/preview mode for the card body
        const text = draft.content || "";
        const snippet = text.length > 240 ? text.substring(0, 240) + "..." : text;

        return (
            <Stack direction="column" spacing={0.75} sx={{width: "100%", maxWidth: 760, mx: "auto", alignItems: "stretch"}}>
                <Typography sx={{
                    color: "rgba(242, 245, 250, 0.85)",
                    fontSize: "0.80rem",
                    fontWeight: 300,
                    lineHeight: 1.6,
                    whiteSpace: "pre-wrap",
                    minHeight: "20px",
                    textAlign: "left",
                }}>
                    {snippet || <span style={{opacity: 0.4, fontStyle: "italic"}}>No content.</span>}
                </Typography>
                {(draft.tags && draft.tags.length > 0) && (
                    <Stack direction="row" spacing={0.5} sx={{flexWrap: "wrap", gap: "4px", pt: 0.5}}>
                        {draft.tags.map((tag) => (
                            <Chip
                                key={tag}
                                label={tag}
                                size="small"
                                sx={{
                                    backgroundColor: "rgba(56, 255, 130, 0.12)",
                                    color: "#38ff82",
                                    border: "1px solid rgba(56, 255, 130, 0.35)",
                                    fontSize: "0.70rem",
                                    height: "20px",
                                }}
                            />
                        ))}
                    </Stack>
                )}
            </Stack>
        );
    }

    return (
        <Stack direction="column" spacing={1.5} sx={{width: "100%", maxWidth: 760, mx: "auto", alignItems: "stretch"}}>
            {/* Metadata header — single flex-wrap row */}
            <Box sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: "4px 16px",
                alignItems: "center",
                padding: "8px 10px",
                backgroundColor: "rgba(0, 0, 0, 0.25)",
                border: "1px solid rgba(63, 70, 79, 0.4)",
                borderRadius: "6px",
            }}>
                <Typography sx={metaLabelSx}>
                    User: <Box component="span" sx={metaValueSx}>{draft.createdBy || "—"}</Box>
                </Typography>
                <Typography sx={metaLabelSx}>
                    UID: <Box component="span" sx={{...metaValueSx, fontFamily: "monospace", fontSize: "0.72rem"}}>{draft.uid || "—"}</Box>
                </Typography>
                <Typography sx={metaLabelSx}>
                    Version: <Box component="span" sx={metaValueSx}>{draft.version || 1}</Box>
                </Typography>
                <Typography sx={metaLabelSx}>
                    Created: <Box component="span" sx={metaValueSx}>{formatDateTime(draft.createdAt) || "—"}</Box>
                </Typography>
            </Box>

            {/* Title — label inside the field */}
            <TextField
                value={draft.title || ""}
                onChange={(e) => upd("title", e.target.value)}
                label="Title"
                size="small"
                fullWidth
                sx={fieldSx}
            />

            {/* Tags — single bordered field with label, input zone in front of inline chips */}
            <Autocomplete
                multiple
                freeSolo
                options={[]}
                value={draft.tags || []}
                onChange={(_e, newValue) => handleTagsChange(newValue as string[])}
                renderTags={(value: readonly string[], getTagProps) =>
                    value.map((tag, index) => {
                        const {key, ...tagProps} = getTagProps({index});
                        return (
                            <Chip
                                key={key}
                                label={tag}
                                size="small"
                                {...tagProps}
                                sx={{
                                    backgroundColor: "rgba(56, 255, 130, 0.12)",
                                    color: "#38ff82",
                                    border: "1px solid rgba(56, 255, 130, 0.35)",
                                    fontSize: "0.72rem",
                                    height: "22px",
                                    "& .MuiChip-deleteIcon": {
                                        color: "rgba(56, 255, 130, 0.7)",
                                        "&:hover": {color: hintColor},
                                    },
                                }}
                            />
                        );
                    })
                }
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Tags"
                        size="small"
                        helperText="Add tag and press Enter"
                        sx={{
                            ...fieldSx,
                            "& .MuiOutlinedInput-root": {
                                ...(fieldSx as Record<string, unknown>)["& .MuiOutlinedInput-root"] as object,
                                height: "auto",
                                minHeight: "38px",
                                padding: "4px 8px",
                                flexWrap: "wrap",
                                gap: "4px",
                            },
                            "& .MuiFormHelperText-root": {
                                color: hintColor,
                                fontSize: "0.65rem",
                                fontStyle: "italic",
                                textAlign: "right",
                                marginLeft: 0,
                                marginRight: "4px",
                                marginTop: "4px",
                            },
                        }}
                    />
                )}
            />

            {/* Content — label inside the field */}
            <TextField
                value={draft.content || ""}
                onChange={(e) => upd("content", e.target.value)}
                label="Content"
                size="small"
                fullWidth
                multiline
                rows={10}
                sx={multilineFieldSx}
            />
        </Stack>
    );
}

export default InsightEditor;
